import { useState, useEffect } from 'react';
import { Building2, Search, Loader2, LogOut, ArrowLeftRight, RefreshCw, AlertCircle } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';
import { cachedFetch } from '../utils/fetchWithCache';

interface Company {
  id: string;
  name: string;
  engagementCount?: number;
}

interface CompanyPickerProps {
  accessToken: string;
  currentUser: any;
  onSelect: (companyId: string, companyName: string) => void;
  onSwitchRole?: () => void;
  onLogout: () => void;
}

const STORAGE_KEY = 'pathkit_active_company';

/** Persist the selected company so refreshes/re-entry restore it. */
export function persistActiveCompany(companyId: string, companyName: string) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: companyId, name: companyName, ts: Date.now() }));
  } catch { /* quota / private browsing */ }
}

/** Read the previously-selected company (may be null). */
export function getPersistedCompany(): { id: string; name: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.id) return parsed;
  } catch { /* ignore */ }
  return null;
}

/** Clear persisted company (e.g. on logout). */
export function clearPersistedCompany() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export function CompanyPicker({ accessToken, currentUser, onSelect, onSwitchRole, onLogout }: CompanyPickerProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Admins previewing the client view pick one of the firm's client companies.
  // Client users who belong to several companies pick their own company.
  const isAdmin = currentUser?.isAdmin || currentUser?.role === 'systemAdmin' || currentUser?.baseRole === 'systemAdmin';

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const base = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

      if (isAdmin) {
        // System admins can see all companies
        const response = await cachedFetch(`${base}/admin/companies`, {
          headers: getAuthHeaders(accessToken),
        });
        if (response.ok) {
          const data = await response.json();
          const list: Company[] = (data.companies || data || []).map((c: any) => ({
            id: c.id,
            name: c.name || c.companyName || 'Unnamed Company',
            engagementCount: c.engagementCount,
          }));
          setCompanies(list);
        } else {
          setError('Failed to load companies');
        }
      } else {
        // Non-admin: try to look up companies by email
        const email = currentUser?.email;
        if (email) {
          const response = await fetch(`${base}/public/lookup-user-companies`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          if (response.ok) {
            const data = await response.json();
            const list: Company[] = (data.companies || []).map((c: any) => ({
              id: c.id || c.companyId,
              name: c.name || c.companyName || 'Unnamed Company',
            }));
            setCompanies(list);
          } else {
            setError('Failed to look up your companies');
          }
        } else {
          setError('No email on file to look up companies');
        }
      }
    } catch (err) {
      console.error('[CompanyPicker] Error loading companies:', err);
      setError('Network error loading companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (company: Company) => {
    persistActiveCompany(company.id, company.name);
    onSelect(company.id, company.name);
  };

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900" style={{ fontWeight: 600 }}>
                  {isAdmin ? 'Select a client company' : 'Select your company'}
                </h2>
                <p className="text-sm text-gray-500">
                  {isAdmin ? 'Choose which client company to work with' : 'Choose which company to work with'}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                <p className="text-sm text-gray-500">Loading available companies...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <AlertCircle className="w-8 h-8 text-amber-400" />
                <p className="text-sm text-gray-600">{error}</p>
                <button
                  onClick={loadCompanies}
                  className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1.5"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retry
                </button>
              </div>
            ) : companies.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Building2 className="w-8 h-8 text-gray-300" />
                <p className="text-sm text-gray-600">No companies available</p>
                <p className="text-xs text-gray-400 text-center max-w-xs">
                  You don't appear to be associated with any companies yet. 
                  Contact your delivery lead or switch to a different role.
                </p>
              </div>
            ) : (
              <>
                {/* Search */}
                {companies.length > 5 && (
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search companies..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400"
                    />
                  </div>
                )}

                {/* Company list */}
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No matches</p>
                  ) : (
                    filtered.map((company) => (
                      <button
                        key={company.id}
                        onClick={() => handleSelect(company)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-purple-50 transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-md bg-gray-100 group-hover:bg-purple-100 flex items-center justify-center flex-shrink-0 transition-colors">
                          <Building2 className="w-4 h-4 text-gray-500 group-hover:text-purple-600 transition-colors" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate" style={{ fontWeight: 500 }}>
                            {company.name}
                          </p>
                          {company.engagementCount !== undefined && (
                            <p className="text-xs text-gray-400">
                              {company.engagementCount} engagement{company.engagementCount !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer with recovery actions */}
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              {onSwitchRole && (
                <button
                  onClick={onSwitchRole}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-purple-600 transition-colors px-2 py-1.5 rounded hover:bg-white"
                >
                  <ArrowLeftRight className="w-3.5 h-3.5" />
                  Switch Role
                </button>
              )}
              <button
                onClick={onLogout}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 transition-colors px-2 py-1.5 rounded hover:bg-white"
              >
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </button>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors px-2 py-1.5 rounded hover:bg-white"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
